import {
  getPossibleVariables,
  type Block,
  type BlockChange,
  type GetVariables,
  type OgImageBlock,
} from '@basehub/data/replicache/index'
import * as React from 'react'

import { clsx } from 'clsx'

import {
  ImageLayer,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  ShapeLayer,
  TextLayer,
} from './input'
import { OgImageLayerContextMenuWrapper } from './context-menu'
import { getOgBlockStore, useLayerFunctions } from './utils'
import { TreeManager } from '@basehub/utils/tree/index'
import { getOgBlockRootOptions } from './vars'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const OgImageBlockInputDiff = ({
  path,
  condition,
  diff,
  treeNew,
  treeOld,
}: {
  path: string
  condition: 'added' | 'removed'
  diff: BlockChange
  treeNew?: TreeManager<Block, NonNullable<unknown>>
  treeOld?: TreeManager<Block, NonNullable<unknown>>
}) => {
  const tree = condition === 'added' ? treeNew : treeOld
  const block = (
    condition === 'added'
      ? (diff.current ?? diff.conflicting ?? diff.previous)
      : (diff.conflicting ?? diff.previous ?? diff.current)
  ) as OgImageBlock

  const status = diff.status

  const repo = useSelectedTeamAndRepo().data?.repo

  const parent = useTreeSlice(tree, (s) => {
    const parentId = s?.getNode(block.id)?.parentKey
    return parentId ? s.getNode(parentId) : null
  })

  const getVariables: GetVariables = React.useCallback(
    (segments) => {
      if (!tree || !repo) return []
      const nodeBlock = parent
      if (!nodeBlock) return []
      try {
        return getPossibleVariables({
          segments,
          nodeBlock,
          treeManager: tree as any,
          valueKey: 'value',
          rootOptions: getOgBlockRootOptions({
            blockTreeManager: tree as any,
            repo,
          }),
          selectedVariants: null,
        })
      } catch (err) {
        console.error(err)
        return []
      }
    },
    [tree, repo, parent]
  )

  const relativeContainerRef = React.useRef<HTMLDivElement>(null)
  const [relativeContainerWidth, setRelativeContainerWidth] = React.useState(0)
  const absoluteContainerRef = React.useRef<HTMLDivElement>(null)

  const floatingLabelTwitter = getOgBlockStore(block.id)(
    (state) => state.floatingLabelTwitter
  )

  const resizeContainers = () => {
    if (absoluteContainerRef.current && relativeContainerRef.current) {
      const relativeContainerWidth = relativeContainerRef.current.offsetWidth // block width

      // Scale the absolute container so it has the full block width and preserve the aspect ratio of the OG image
      const scaleFactor = relativeContainerWidth / OG_IMAGE_WIDTH
      absoluteContainerRef.current.style.transform = `scale(${scaleFactor})`

      // Set the height of the relative container so it has the same aspect ratio as the OG image
      const relativeContainerHeight =
        relativeContainerWidth * (OG_IMAGE_HEIGHT / OG_IMAGE_WIDTH)

      relativeContainerRef.current.style.height = `${relativeContainerHeight}px`
    }
  }

  React.useEffect(() => {
    resizeContainers()

    const handleResize = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        setRelativeContainerWidth(entry.contentRect.width)
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)

    window.addEventListener('resize', resizeContainers)
    if (relativeContainerRef.current) {
      resizeObserver.observe(relativeContainerRef.current)
    }

    return () => {
      window.removeEventListener('resize', resizeContainers)
      if (relativeContainerRef.current) {
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserver.unobserve(relativeContainerRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    resizeContainers()
  }, [relativeContainerWidth])

  const value = React.useMemo(
    () => ({ ...block.constraints, ...block.value }),
    [block.constraints, block.value]
  )

  const fontsCSS = React.useMemo(() => {
    const usedFonts = value?.layersData
      .filter((layer) => layer.type === 'text')
      .map((layer) => {
        if (layer.type === 'text') {
          return {
            fontFamily: layer.fontFamily,
            fontUrl: layer.fontUrl,
            fontWeight: layer.fontWeight,
          }
        }
      })
    // Remove duplicates
    const uniqueFonts = [...new Set(usedFonts)]

    return `
          ${uniqueFonts
            .map(
              (font) => `
            @font-face {
              font-family: '${font?.fontFamily}';
              src: url('${font?.fontUrl}') format('truetype');
              font-weight: ${font?.fontWeight};
              fallback: block;
            }
          `
            )
            .join('')}
        `
  }, [value])

  const layerFunctions = useLayerFunctions({
    block,
    path,
    parent: parent?.value,
  })

  return (
    <div
      className={clsx(
        'p-1 overflow-hidden rounded border-dashed',
        condition === 'added' && status === 'created' && 'border border-green',
        condition === 'removed' && status === 'deleted' && 'border border-red',
        status === 'modified' && 'border border-yellow',
        condition === 'removed' && status === 'created' && 'opacity-30',
        condition === 'added' && status === 'deleted' && 'opacity-30'
      )}
    >
      <div className="flex flex-col gap-3">
        <style>{fontsCSS}</style>
        <div
          ref={relativeContainerRef}
          className="relative w-full flex justify-center items-center rounded"
        >
          <div
            ref={absoluteContainerRef}
            className="absolute h-[630px] w-[1200px]"
          >
            {floatingLabelTwitter && (
              <div className="absolute bottom-8 left-8 right-8 z-50 line-clamp-1 w-fit">
                <div className="flex h-14 items-center justify-center self-start rounded-[8px] bg-black/30 px-2">
                  <span className="line-clamp-1 break-words break-all text-left text-[38px] font-normal leading-8 text-dark-bright pl-1">
                    Your site's title will be rendered here in the X feed.
                  </span>
                </div>
              </div>
            )}

            <div className="flex h-full w-full">
              {/* This is replicated with satori on og generation*/}
              {value.layersData
                .slice()
                .sort((a, b) => a.positionZ - b.positionZ)
                .map((layer, i) => {
                  return (
                    <React.Fragment key={i}>
                      {layer.positionZ === 0 && layer.type === 'shape' ? (
                        // Background Layer
                        <div
                          key={`${block.id}-backgroundLayer`}
                          className="select-none appearance-none focus:outline-none"
                          style={{
                            position: 'absolute',
                            left: layer.positionX,
                            top: layer.positionY,
                            transform: `rotate(${layer.rotation}deg)`,
                            width: layer.width,
                            height: layer.height,
                            borderRadius: layer.cornerRadius,
                            opacity: layer.opacity,
                            backgroundColor: layer.color,
                          }}
                        />
                      ) : (
                        <OgImageLayerContextMenuWrapper
                          layer={layer}
                          block={block}
                          path={path}
                          layerFunctions={layerFunctions}
                        >
                          {layer.type === 'text' ? (
                            <TextLayer
                              key={`${block.id}-${layer.positionZ}`}
                              layer={layer}
                              getVariables={getVariables}
                              id={`${block.id}-layer-${i}`}
                            />
                          ) : layer.type === 'image' ? (
                            <ImageLayer
                              key={`${block.id}-${layer.positionZ}`}
                              layer={layer}
                              getVariables={getVariables}
                            />
                          ) : (
                            <ShapeLayer
                              key={`${block.id}-${layer.positionZ}`}
                              layer={layer}
                              id={`${block.id}-layer-${i}`}
                            />
                          )}
                        </OgImageLayerContextMenuWrapper>
                      )}
                    </React.Fragment>
                  )
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
